import { and, asc, desc, eq, ilike, sql } from "drizzle-orm";
import type { DbClient } from "@/server/platform/db/client";
import {
  goodsReceiptLines,
  goodsReceipts,
  stockLots,
} from "@/server/platform/db/schema/inventory";
import { nowDate } from "@/shared/lib/date-time";
import type { DbTransaction } from "@/shared/types";
import type { GoodsReceiptListQueryDTO } from "../contracts";
import type {
  GoodsReceiptDetailDTO,
  GoodsReceiptLineDTO,
  GoodsReceiptListItemDTO,
  StockLotDetailDTO,
} from "../types";
import { listStockLotsForReceipt } from "./stock-lots";

type DbLike = DbTransaction | DbClient;

type ReceiptHeaderInsert = {
  receiptNumber: string;
  receivedAt: Date;
  carrier: string | null;
  carrierNote: string | null;
  trackingNumber: string | null;
  shippingFeeTotal: string;
  otherFeesTotal: string;
  shippingAllocationMethod: string;
  note: string | null;
  importOrderId?: string | null;
  createdBy: string | null;
};

type ReceiptLineInsert = {
  variantId: string;
  quantity: number;
  stockQuantity: number;
  conversionFactor: string;
  stockUnitLabel: string;
  receiveUnitLabel: string | null;
  unitPurchaseCost: string;
  shippingAllocated?: string;
  productName: string;
  variantName: string;
};

export async function listGoodsReceipts(
  client: DbLike,
  query: GoodsReceiptListQueryDTO,
): Promise<GoodsReceiptListItemDTO[]> {
  const conditions = [];
  if (query.status) conditions.push(eq(goodsReceipts.status, query.status));
  if (query.carrier) conditions.push(eq(goodsReceipts.carrier, query.carrier));
  if (query.search?.trim()) {
    conditions.push(
      ilike(goodsReceipts.receiptNumber, `%${query.search.trim()}%`),
    );
  }

  const rows = await client
    .select({
      id: goodsReceipts.id,
      receiptNumber: goodsReceipts.receiptNumber,
      status: goodsReceipts.status,
      receivedAt: goodsReceipts.receivedAt,
      carrier: goodsReceipts.carrier,
      carrierNote: goodsReceipts.carrierNote,
      trackingNumber: goodsReceipts.trackingNumber,
      shippingFeeTotal: goodsReceipts.shippingFeeTotal,
      purchaseSubtotal: goodsReceipts.purchaseSubtotal,
      landedSubtotal: goodsReceipts.landedSubtotal,
      createdAt: goodsReceipts.createdAt,
      updatedAt: goodsReceipts.updatedAt,
      lineCount: sql<number>`(
        select count(*)::int from ${goodsReceiptLines}
        where ${goodsReceiptLines.receiptId} = ${goodsReceipts.id}
      )`,
      totalQuantity: sql<number>`(
        select coalesce(sum(${goodsReceiptLines.quantity}), 0)::int from ${goodsReceiptLines}
        where ${goodsReceiptLines.receiptId} = ${goodsReceipts.id}
      )`,
      totalStockQuantity: sql<number>`(
        select coalesce(sum(${goodsReceiptLines.stockQuantity}), 0)::int from ${goodsReceiptLines}
        where ${goodsReceiptLines.receiptId} = ${goodsReceipts.id}
      )`,
    })
    .from(goodsReceipts)
    .where(conditions.length ? and(...conditions) : undefined)
    .orderBy(desc(goodsReceipts.createdAt));

  return rows.map((r) => ({
    ...r,
    status: r.status as GoodsReceiptListItemDTO["status"],
    carrier: r.carrier as GoodsReceiptListItemDTO["carrier"],
  }));
}

export async function listGoodsReceiptsByImportOrderId(
  client: DbLike,
  importOrderId: string,
): Promise<GoodsReceiptListItemDTO[]> {
  const rows = await client
    .select({
      id: goodsReceipts.id,
      receiptNumber: goodsReceipts.receiptNumber,
      status: goodsReceipts.status,
      receivedAt: goodsReceipts.receivedAt,
      carrier: goodsReceipts.carrier,
      carrierNote: goodsReceipts.carrierNote,
      trackingNumber: goodsReceipts.trackingNumber,
      shippingFeeTotal: goodsReceipts.shippingFeeTotal,
      purchaseSubtotal: goodsReceipts.purchaseSubtotal,
      landedSubtotal: goodsReceipts.landedSubtotal,
      createdAt: goodsReceipts.createdAt,
      updatedAt: goodsReceipts.updatedAt,
      lineCount: sql<number>`(
        select count(*)::int from ${goodsReceiptLines}
        where ${goodsReceiptLines.receiptId} = ${goodsReceipts.id}
      )`,
      totalQuantity: sql<number>`(
        select coalesce(sum(${goodsReceiptLines.quantity}), 0)::int from ${goodsReceiptLines}
        where ${goodsReceiptLines.receiptId} = ${goodsReceipts.id}
      )`,
      totalStockQuantity: sql<number>`(
        select coalesce(sum(${goodsReceiptLines.stockQuantity}), 0)::int from ${goodsReceiptLines}
        where ${goodsReceiptLines.receiptId} = ${goodsReceipts.id}
      )`,
    })
    .from(goodsReceipts)
    .where(eq(goodsReceipts.importOrderId, importOrderId))
    .orderBy(desc(goodsReceipts.createdAt));

  return rows.map((r) => ({
    ...r,
    status: r.status as GoodsReceiptListItemDTO["status"],
    carrier: r.carrier as GoodsReceiptListItemDTO["carrier"],
  }));
}

export async function getGoodsReceiptById(
  client: DbLike,
  id: string,
): Promise<GoodsReceiptDetailDTO | null> {
  const [row] = await client
    .select()
    .from(goodsReceipts)
    .where(eq(goodsReceipts.id, id));
  if (!row) return null;

  const lines = await client
    .select()
    .from(goodsReceiptLines)
    .where(eq(goodsReceiptLines.receiptId, id))
    .orderBy(asc(goodsReceiptLines.productName));

  let lots: StockLotDetailDTO[] | undefined;
  if (row.status === "confirmed") {
    lots = await listStockLotsForReceipt(client, id);
  }

  return {
    id: row.id,
    receiptNumber: row.receiptNumber,
    status: row.status as GoodsReceiptDetailDTO["status"],
    receivedAt: row.receivedAt,
    carrier: row.carrier as GoodsReceiptDetailDTO["carrier"],
    carrierNote: row.carrierNote,
    trackingNumber: row.trackingNumber,
    shippingFeeTotal: row.shippingFeeTotal,
    otherFeesTotal: row.otherFeesTotal,
    shippingAllocationMethod:
      row.shippingAllocationMethod as GoodsReceiptDetailDTO["shippingAllocationMethod"],
    purchaseSubtotal: row.purchaseSubtotal,
    landedSubtotal: row.landedSubtotal,
    note: row.note,
    importOrderId: row.importOrderId,
    createdBy: row.createdBy,
    confirmedAt: row.confirmedAt,
    createdAt: row.createdAt,
    updatedAt: row.updatedAt,
    lines: lines.map(
      (l): GoodsReceiptLineDTO => ({
        id: l.id,
        receiptId: l.receiptId,
        variantId: l.variantId,
        quantity: l.quantity,
        stockQuantity: l.stockQuantity,
        conversionFactor: l.conversionFactor,
        stockUnitLabel: l.stockUnitLabel,
        receiveUnitLabel: l.receiveUnitLabel,
        unitPurchaseCost: l.unitPurchaseCost,
        shippingAllocated: l.shippingAllocated,
        unitLandedCost: l.unitLandedCost,
        lineLandedTotal: l.lineLandedTotal,
        productName: l.productName,
        variantName: l.variantName,
      }),
    ),
    lots,
  };
}

export async function insertGoodsReceipt(
  client: DbTransaction,
  header: ReceiptHeaderInsert,
  lines: ReceiptLineInsert[],
) {
  const now = nowDate();
  const [receipt] = await client
    .insert(goodsReceipts)
    .values({
      receiptNumber: header.receiptNumber,
      status: "draft",
      receivedAt: header.receivedAt,
      carrier: header.carrier,
      carrierNote: header.carrierNote,
      trackingNumber: header.trackingNumber,
      shippingFeeTotal: header.shippingFeeTotal,
      otherFeesTotal: header.otherFeesTotal,
      shippingAllocationMethod: header.shippingAllocationMethod,
      note: header.note,
      importOrderId: header.importOrderId ?? null,
      createdBy: header.createdBy,
      createdAt: now,
      updatedAt: now,
    })
    .returning();
  if (!receipt) throw new Error("Failed to create goods receipt");

  await client.insert(goodsReceiptLines).values(
    lines.map((l) => ({
      receiptId: receipt.id,
      variantId: l.variantId,
      quantity: l.quantity,
      stockQuantity: l.stockQuantity,
      conversionFactor: l.conversionFactor,
      stockUnitLabel: l.stockUnitLabel,
      receiveUnitLabel: l.receiveUnitLabel,
      unitPurchaseCost: l.unitPurchaseCost,
      ...(l.shippingAllocated !== undefined
        ? { shippingAllocated: l.shippingAllocated }
        : {}),
      productName: l.productName,
      variantName: l.variantName,
    })),
  );

  return receipt;
}

export async function updateGoodsReceiptHeader(
  client: DbTransaction,
  id: string,
  patch: Partial<ReceiptHeaderInsert> & {
    purchaseSubtotal?: string;
    landedSubtotal?: string;
    status?: string;
    confirmedAt?: Date | null;
  },
) {
  const [row] = await client
    .update(goodsReceipts)
    .set({
      ...(patch.receivedAt !== undefined
        ? { receivedAt: patch.receivedAt }
        : {}),
      ...(patch.carrier !== undefined ? { carrier: patch.carrier } : {}),
      ...(patch.carrierNote !== undefined
        ? { carrierNote: patch.carrierNote }
        : {}),
      ...(patch.trackingNumber !== undefined
        ? { trackingNumber: patch.trackingNumber }
        : {}),
      ...(patch.shippingFeeTotal !== undefined
        ? { shippingFeeTotal: patch.shippingFeeTotal }
        : {}),
      ...(patch.otherFeesTotal !== undefined
        ? { otherFeesTotal: patch.otherFeesTotal }
        : {}),
      ...(patch.shippingAllocationMethod !== undefined
        ? { shippingAllocationMethod: patch.shippingAllocationMethod }
        : {}),
      ...(patch.note !== undefined ? { note: patch.note } : {}),
      ...(patch.importOrderId !== undefined
        ? { importOrderId: patch.importOrderId }
        : {}),
      ...(patch.purchaseSubtotal !== undefined
        ? { purchaseSubtotal: patch.purchaseSubtotal }
        : {}),
      ...(patch.landedSubtotal !== undefined
        ? { landedSubtotal: patch.landedSubtotal }
        : {}),
      ...(patch.status !== undefined ? { status: patch.status } : {}),
      ...(patch.confirmedAt !== undefined
        ? { confirmedAt: patch.confirmedAt }
        : {}),
      updatedAt: nowDate(),
    })
    .where(eq(goodsReceipts.id, id))
    .returning();
  return row ?? null;
}

export async function replaceGoodsReceiptLines(
  client: DbTransaction,
  receiptId: string,
  lines: ReceiptLineInsert[],
) {
  await client
    .delete(goodsReceiptLines)
    .where(eq(goodsReceiptLines.receiptId, receiptId));
  if (lines.length === 0) return;
  await client.insert(goodsReceiptLines).values(
    lines.map((l) => ({
      receiptId,
      variantId: l.variantId,
      quantity: l.quantity,
      stockQuantity: l.stockQuantity,
      conversionFactor: l.conversionFactor,
      stockUnitLabel: l.stockUnitLabel,
      receiveUnitLabel: l.receiveUnitLabel,
      unitPurchaseCost: l.unitPurchaseCost,
      ...(l.shippingAllocated !== undefined
        ? { shippingAllocated: l.shippingAllocated }
        : {}),
      productName: l.productName,
      variantName: l.variantName,
    })),
  );
}

/** อัปเดต landed cost ต่อบรรทัด (ตอน confirm) */
export async function updateLineLandedCost(
  client: DbTransaction,
  lineId: string,
  patch: {
    shippingAllocated: string;
    unitLandedCost: string;
    lineLandedTotal: string;
  },
) {
  await client
    .update(goodsReceiptLines)
    .set(patch)
    .where(eq(goodsReceiptLines.id, lineId));
}

export async function insertStockLot(
  client: DbTransaction,
  input: {
    receiptId: string;
    receiptLineId: string;
    variantId: string;
    quantityReceived: number;
    unitPurchaseCost: string;
    unitLandedCost: string;
    receivedAt: Date;
  },
) {
  await client.insert(stockLots).values({
    receiptId: input.receiptId,
    receiptLineId: input.receiptLineId,
    variantId: input.variantId,
    quantityReceived: input.quantityReceived,
    quantityRemaining: input.quantityReceived,
    unitPurchaseCost: input.unitPurchaseCost,
    unitLandedCost: input.unitLandedCost,
    receivedAt: input.receivedAt,
  });
}

/** lot ที่ผูกกับใบรับเข้า — ใช้ตอน void เพื่อตรวจว่าถูกขายไปแล้วหรือยัง */
export async function getStockLotsByReceipt(client: DbLike, receiptId: string) {
  return client
    .select()
    .from(stockLots)
    .where(eq(stockLots.receiptId, receiptId));
}

export async function deleteStockLotsByReceipt(
  client: DbTransaction,
  receiptId: string,
) {
  await client.delete(stockLots).where(eq(stockLots.receiptId, receiptId));
}

/** ลบใบรับเข้า draft ถาวน — lines cascade ตาม FK */
export async function hardDeleteGoodsReceipt(
  client: DbTransaction,
  id: string,
) {
  await client.delete(goodsReceipts).where(eq(goodsReceipts.id, id));
}
